// Global Instructions Rule Applied!
//
// Settings → My signature.
//
// Edit your personal signature_html override. Empty/cleared = revert to the
// firm default (cfg.app_config 'email/signature_default_html'). Live preview
// pane shows the FULLY MERGED HTML (i.e. {{user.*}} substituted against your
// system_users row) so what you see is what recipients see.
//
// Saving updates only your row. Existing drafts ALREADY have their signature
// snapshotted into body_html (see plan risks "signature drift") — this save
// does NOT retroactively update them, by design.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Col, Row, Space, Spin, Typography } from 'antd'
import { SaveOutlined, UndoOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import RichTextEditor from '../../../core/components/RichTextEditor.jsx'
import type { CKEditorRichTextHandle } from '../../../core/components/CKEditorRichText.tsx'
import { useEditorEngine, useEditorConfigReady } from '../../global_configs/hooks/useEditorConfig.ts'
import { ShadowRichHtml } from '../../../core/components/ShadowRichHtml.tsx'
import { fetchMySignature, saveMySignature } from '../../email/services/emailService.ts'
import type { EmailMySignatureModel } from '../../../state_manager/data_models/email_campaign_grid_model.ts'

const { Text } = Typography

// Lazy-loaded (code-split; editor build loads from the CDN). Mirrors Full.tsx.
const CKEditorRichText = React.lazy(
  () => import('../../../core/components/CKEditorRichText.tsx')
)

export function MySignatureTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [model, setModel] = useState<EmailMySignatureModel | null>(null)
  // The Jodit editor edits the TEMPLATE (raw override), not the resolved
  // version — so the user can keep merge placeholders like {{user.email}}
  // in their signature and have them resolve per-send.
  const [editorValue, setEditorValue] = useState<string>('')
  const [previewHtml, setPreviewHtml] = useState<string>('')
  // Editor engine (cfg-driven). 'ckeditor' swaps in CKEditor and inline-at-
  // saves the signature so the stored HTML is self-contained for email.
  const editorEngine = useEditorEngine()
  // Gate the editor until globalConfigs loads (avoids a cold-load Jodit flash).
  const engineReady = useEditorConfigReady()
  const editorRef = useRef<CKEditorRichTextHandle | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    const res = await fetchMySignature(token)
    if (res.error) {
      showErrorToast(res.error)
      setLoading(false)
      return
    }
    if (res.data) {
      setModel(res.data)
      setEditorValue(res.data.signature_html || '')
      setPreviewHtml(res.data.resolved_html || '')
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleEditorChange = useCallback(
    (next: string) => {
      setEditorValue(next)
      // Best-effort live preview by substituting against the system_user we
      // loaded with the page. The authoritative resolved_html comes back on
      // save (or on revert).
      if (model?.system_user) {
        let preview = next || model.firm_default_html
        const u = model.system_user
        const subs: Record<string, string> = {
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || '',
          work_title: u.work_title || '',
          mobile_phone: u.mobile_phone || ''
        }
        for (const [k, v] of Object.entries(subs)) {
          preview = preview.replace(new RegExp(`\\{\\{\\s*user\\.${k}\\s*\\}\\}`, 'g'), v)
        }
        setPreviewHtml(preview)
      }
    },
    [model]
  )

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      // Inline-at-save when CKEditor (plan §6.1): store a self-contained
      // inline-styled signature so it renders correctly in email regardless
      // of which engine composes the campaign. {{user.*}} placeholders are
      // plain text and survive the inline export untouched. Empty → null
      // (revert to firm default).
      let signatureHtml = editorValue || ''
      if (editorEngine === 'ckeditor' && signatureHtml.trim()) {
        try {
          const inline = await editorRef.current?.getInlineHtml()
          if (inline && inline.trim()) signatureHtml = inline
        } catch {
          /* keep the class-based fallback */
        }
      }
      const res = await saveMySignature(
        { signature_html: signatureHtml || null },
        token
      )
      if (!res.success) {
        showErrorToast(res.error || 'Failed to save signature')
        return
      }
      toast.success('Signature saved')
      await refresh()
    } finally {
      setSaving(false)
    }
  }, [editorValue, editorEngine, refresh])

  const handleRevertToDefault = useCallback(async () => {
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await saveMySignature({ signature_html: null }, token)
      if (!res.success) {
        showErrorToast(res.error || 'Failed to revert')
        return
      }
      toast.success('Reverted to firm default')
      await refresh()
    } finally {
      setSaving(false)
    }
  }, [refresh])

  return (
    <Spin spinning={loading}>
      <div style={{ padding: '8px 0 24px 0' }}>
        <Alert
          showIcon
          type='info'
          style={{ marginBottom: 16 }}
          message="Existing drafts won't change"
          description='Drafts snapshot the signature into the body at creation. Changing your signature here applies only to new drafts.'
        />

        <Row gutter={20}>
          <Col xs={24} md={14}>
            <Card
              title='My signature'
              extra={
                <Space>
                  <Button
                    icon={<UndoOutlined />}
                    onClick={handleRevertToDefault}
                    disabled={!model || model.used_firm_default}
                    loading={saving}
                  >
                    Use firm default
                  </Button>
                  <Button
                    type='primary'
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </Space>
              }
              bodyStyle={{ padding: 0 }}
            >
              <div style={{ height: 380 }}>
                {!engineReady ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin />
                  </div>
                ) : editorEngine === 'ckeditor' ? (
                  <React.Suspense
                    fallback={
                      <div style={{ padding: 24, textAlign: 'center' }}>
                        <Spin />
                      </div>
                    }
                  >
                    <CKEditorRichText
                      ref={editorRef}
                      value={editorValue}
                      onChange={handleEditorChange}
                      placeholder='Your signature (use {{user.first_name}}, {{user.last_name}}, {{user.email}}, {{user.work_title}}, {{user.mobile_phone}} for merge fields)…'
                      imageUpload={false}
                    />
                  </React.Suspense>
                ) : (
                  <RichTextEditor
                    value={editorValue}
                    onChange={handleEditorChange}
                    placeholder='Your signature (use {{user.first_name}}, {{user.last_name}}, {{user.email}}, {{user.work_title}}, {{user.mobile_phone}} for merge fields)…'
                  />
                )}
              </div>
            </Card>
            <Text type='secondary' style={{ display: 'block', marginTop: 8 }}>
              {model?.used_firm_default
                ? 'Currently using the firm-wide default signature.'
                : 'Personal signature override active.'}
            </Text>
          </Col>

          <Col xs={24} md={10}>
            <Card title='Preview (with merge fields)' bodyStyle={{ padding: 0 }}>
              <ShadowRichHtml
                html={
                  previewHtml ||
                  '<p style="color:#bfbfbf">(preview will appear here)</p>'
                }
                className='block'
              />
            </Card>
          </Col>
        </Row>
      </div>
    </Spin>
  )
}
